import * as angular from 'angular';

import { ComponentDefinition, Ticket, Directory, IFileInformation } from '../../models';
import { defaultTicketAllowedFileTypes, Roles } from '../../common';
import { IAuthenticationService, IAuthorizationService, ITicketAttachmentService, IPdfToSvgService } from '../../services';
import { getBase64, getArrayBuffer } from './getBase64';
import { GuidHelper } from '../../framework';

class TicketModalAttachmentsController implements angular.IComponentController {
    fileBase64: string;
    hideUpload: boolean;
    isLoading: boolean;
    loadingMessage: string;
    message: string;
    messageClass: string;
    rawItems: Directory = {} as Directory;
    selectedFile: Blob;
    selectedFileList: FileList;
    selectedItem: File;
    ticketUploadAllowedFileTypes: string[];
    ticketUploadMaxSize: number;
    types: string;

    readonly canUpdateTitleAndDescription: boolean;
    readonly currentTicketModalPage: number;
    readonly files: File[];
    readonly formController: angular.IFormController;
    readonly randomGuid: string;
    readonly ticket: Ticket;

    static $inject = [
        '$scope',
        '$translate',
        'ticketAttachmentService',
        'authenticationService',
        'authorizationService',
        'pdfToSvgService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _ticketAttachmentService: ITicketAttachmentService,
        private _authenticationService: IAuthenticationService,
        private _authorizationService: IAuthorizationService,
        private _pdfToSvgService: IPdfToSvgService
    ) {
        this.randomGuid = GuidHelper.getGuid();
    }

    $onInit() {
        // Hide upload if user is not Master, the ticket creator or the ticket assignee
        const isMaster = this._authorizationService.isInRole(Roles.master);
        const isAssignee = this._authenticationService.authentication.userId === this.ticket.assignedUser;
        const isCreator = this._authenticationService.authentication.userId === this.ticket.creationUser;
        this.hideUpload = !(isMaster || isCreator || isAssignee);

        this._ticketAttachmentService
            .settings()
            .then(data => {
                this.ticketUploadMaxSize = data.maxSize;
                this.ticketUploadAllowedFileTypes = data.allowedTypes;
                this.types = this.ticketUploadAllowedFileTypes.join(', ');
            });

        this._$scope.$watch(
            () => this.selectedItem,
            () => this.onSelectedItemChange()
        );

        // On file to upload selected
        this._$scope.$watch(
            () => this.selectedFileList,
            () => this.onSelectedFileListChange()
        );
    }

    $onChanges(changes: angular.IOnChangesObject) {
        const types: string[] = changes['ticketUploadAllowedFileTypes']?.currentValue;
        if (types) {
            this.types = (types?.length > 0)
                ? types.join(', ')
                : defaultTicketAllowedFileTypes.join(', ');
        }

        if (changes['ticket']) {
            this.selectedItem = null;
            this.selectedFile = null;
            this.message = null;
            this.onSelectedItemChange();
        }

        if (changes['files']) {
            this.rawItems = {
                name: '',
                relativePath: '',
                directories: [],
                files: []
            };
        }
    }

    removeRawItem(item: IFileInformation) {
        const index = this.files.findIndex(f => f.name === item.name);
        if (index >= 0) {
            this.files.splice(index, 1);
        }
    }

    private onSelectedFileListChange() {
        if (this.selectedFileList && this.selectedFileList.length === 1) {
            if (this.ticket && this.ticket.id) {
                // Add file to existing ticket by direct upload
                this.uploadFilesToFolder(this.selectedFileList[0], this.ticket.id);
            } else {
                // Add file to new ticket, no Id available
                // Store file temporary in memory
                // Upload the files when the ticket is saved
                this.addFileToUploadList(this.selectedFileList[0] as IFileInformation);
            }
        }
    }

    private onSelectedItemChange() {
        this.fileBase64 = null;
        if (this.selectedItem) {
            if (this.rawItems.files && this.rawItems.files.length > 0) {
                const foundFile = this.rawItems.files.find(file => file.name === this.selectedItem.name);
                if (foundFile) {
                    if (foundFile?.type?.indexOf('/pdf') >= 0) {
                        getArrayBuffer(foundFile, result => {
                            this._pdfToSvgService.convertToBase64(result, true)
                                .then(data => this.fileBase64 = data);
                        });
                    } else {
                        getBase64(foundFile, result => this.fileBase64 = result);
                    }
                }
            }
        }
    }

    private uploadFilesToFolder(fileToUpload: File, ticketId: number): void {
        if (fileToUpload) {
            const doRefresh = !!fileToUpload;
            if (fileToUpload && fileToUpload.size >= this.ticketUploadMaxSize) {
                this.messageClass = 'alert alert-danger';
                this.message = this._$translate.instant('File too large') + ': ' + fileToUpload.name;
                this.selectedFileList = undefined;
                this.formController.$setPristine();
            } else {
                this.messageClass = 'alert alert-success';
                this.message = null;
                this.isLoading = true;
                this._ticketAttachmentService
                    .uploadAttachment(ticketId, fileToUpload)
                    .then(data => {
                        if (data.isSuccessful) {
                            this.messageClass = 'alert alert-success';
                            this.message = this._$translate.instant('File uploaded');
                            if (doRefresh) {
                                this.addRawItem(fileToUpload);
                            }
                            this.messageClass = 'alert alert-success';
                            this.message = undefined;
                        } else {
                            this.messageClass = 'alert alert-danger';
                            this.message = this._$translate.instant(data.message);
                        }
                    })
                    .catch(error => {
                        this.messageClass = 'alert alert-danger';
                        this.message = this._$translate.instant('File cannot be uploaded') + ', ' + error;
                    })
                    .finally(() => {
                        this.selectedFileList = undefined;
                        this.formController.$setPristine();
                        this.isLoading = false;
                    });
            }
        }
    }

    private addFileToUploadList(fileToUpload: IFileInformation) {
        if (fileToUpload && fileToUpload.size >= this.ticketUploadMaxSize) {
            this.messageClass = 'alert alert-danger';
            this.message = this._$translate.instant('File too large') + ': ' + fileToUpload.name;
            this.selectedFileList = undefined;
        } else {
            if (!this.rawItems.files) {
                this.rawItems.files = [];
            }
            const foundFile = this.rawItems.files.find(file => file.name === fileToUpload.name);
            if (this.rawItems.files && !foundFile) {
                if (!this.ticket || !this.ticket.id) {
                    this.addRawItem(fileToUpload);
                    this.files.push(fileToUpload);
                    this.messageClass = 'alert alert-success';
                    this.message = this._$translate.instant('File added') + ': ' + fileToUpload.name;
                }
            }
        }
    }

    private addRawItem(file: File) {
        const attachment = { name: file.name, size: file.size } as IFileInformation;
        const raws = {} as Directory;
        raws.directories = [];
        raws.name = '';
        raws.files = this.rawItems?.files ?? [];
        raws.files.push(attachment);
        this.rawItems = raws;
        this.formController.$setDirty();
    }
}

export const wtTicketModalAttachments: ComponentDefinition = {
    componentId: 'wtTicketModalAttachments',
    controller: TicketModalAttachmentsController,
    template: require('./ticketModalAttachments.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=',
        currentTicketModalPage: '=',
        ticket: '<',
        canUpdateTitleAndDescription: '<',
        files: '<'
    }
};